import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shell({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#090A0E] px-4 text-slate-200">
      <div className="grain-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#12141C]/90 p-8 text-center shadow-[0_0_60px_rgba(0,0,0,0.6)] backdrop-blur-md sm:p-10"
      >
        <img src="/media/logo.png" alt="Elite Mobile Detail" className="mx-auto mb-8 h-14 w-auto" />
        {children}
      </motion.div>
    </div>
  );
}

const btn =
  "mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#FFB703] px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#FFA200] hover:shadow-[0_0_36px_rgba(255,183,3,0.4)]";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState({ phase: "error" });
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ phase: "paid", data });
        if (["expired", "failed"].includes(data.payment_status)) return setState({ phase: "error" });
      } catch {
        return setState({ phase: "error" });
      }
      if (++attempts.current >= 6) return setState({ phase: "timeout" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      {state.phase === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#FFB703]" />
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-slate-400">Confirming your deposit…</p>
        </div>
      )}
      {state.phase === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#FFB703]" />
          <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Booking Secured</h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Your <span className="font-mono text-[#FFB703]" data-testid="payment-amount">${state.data.amount.toFixed(2)} {state.data.currency}</span> deposit
            is confirmed. We’ll be in touch shortly to lock in your appointment time. Balance is payable on the day.
          </p>
          <Link to="/" data-testid="payment-back-home" className={btn}><ArrowLeft className="h-4 w-4" /> Back to site</Link>
        </div>
      )}
      {(state.phase === "timeout" || state.phase === "error") && (
        <div data-testid="payment-pending">
          <Loader2 className="mx-auto h-12 w-12 text-slate-500" />
          <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white">Still Processing</h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            We haven’t received confirmation from Stripe yet. If you completed payment, you’ll get a receipt by email — no need to pay again.
          </p>
          <Link to="/" data-testid="payment-back-home" className={btn}><ArrowLeft className="h-4 w-4" /> Back to site</Link>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-cancelled">
        <XCircle className="mx-auto h-12 w-12 text-slate-500" />
        <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Payment Cancelled</h1>
        <p className="mt-4 text-sm leading-relaxed text-slate-400">
          No charge was made. Your quote request is still saved — we’ll reach out to confirm your booking, or you can pay the deposit later.
        </p>
        <Link to="/" data-testid="payment-back-home" className={btn}><ArrowLeft className="h-4 w-4" /> Back to site</Link>
      </div>
    </Shell>
  );
}
